import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Check,
  Star,
  Lightning,
  Rocket,
  ArrowUp,
  Lock,
  Receipt,
  WarningCircle,
  ArrowClockwise,
  Clock,
  Bank,
  CaretDown,
  CaretUp,
} from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";
import StatusBadge from "@/components/StatusBadge";

const PLAN_ICONS = { starter: Star, pro: Lightning, scale: Rocket };

export default function Billing() {
  const { user } = useAuth();
  if (user.role === "super_admin") return <SuperAdminBilling />;
  return <CompanyAdminBilling />;
}

/* =====================  Company Admin self-serve  ===================== */
function CompanyAdminBilling() {
  const { user, setUser } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, inv] = await Promise.all([
        api.get("/billing/my-subscription"),
        api.get("/invoices", { params: { page_size: 100 } }),
      ]);
      setSub(s.data);
      setInvoices(inv.data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    }
  };

  useEffect(() => { load(); }, []);

  // Refresh after WiPay redirect (response_url drops user back here)
  useEffect(() => {
    const wid = params.get("wipay_invoice_id");
    if (!wid) return;
    toast.success("Refreshing payment status…");
    const t = setTimeout(() => { load(); navigate("/billing", { replace: true }); }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [params]);

  const payNow = async (inv) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/wipay/checkout`);
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("No payment URL returned");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Payment init failed");
    } finally {
      setBusy(false);
    }
  };

  const changePlan = async (planId) => {
    if (!confirm(`Change subscription to ${planId.toUpperCase()}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/billing/change-plan", { plan_id: planId });
      toast.success(data.message || (data.scheduled ? "Plan change scheduled" : "Plan changed"));
      try { const me = await api.get("/auth/me"); setUser(me.data); } catch (e) {}
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Change failed");
    } finally {
      setBusy(false);
    }
  };

  const cancelScheduled = async () => {
    if (!confirm("Cancel the scheduled plan change?")) return;
    setBusy(true);
    try {
      await api.post("/billing/cancel-scheduled-change");
      toast.success("Scheduled change cancelled");
      try { const me = await api.get("/auth/me"); setUser(me.data); } catch (e) {}
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Cancel failed");
    } finally {
      setBusy(false);
    }
  };

  if (!sub) {
    return <div className="p-8 text-zinc-500" data-testid="billing-loading">Loading subscription…</div>;
  }

  const currentPlan = sub.plan;
  const unpaid = (invoices || []).filter((i) => i.status === "unpaid");
  const paid = (invoices || []).filter((i) => i.status === "paid").slice(0, 10);

  return (
    <div className="p-6 sm:p-8 max-w-[1200px]" data-testid="billing-page">
      <Toaster position="top-right" />
      <div className="mb-8">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">Subscription</div>
        <h1 className="text-3xl font-semibold tracking-tight mt-1 text-zinc-900 dark:text-zinc-100" style={{ fontFamily: "Work Sans" }}>
          Billing
        </h1>
        <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">Manage your subscription and pay outstanding invoices.</p>
      </div>

      {/* Locked banner */}
      {sub.account_locked && (
        <div
          data-testid="account-locked-banner"
          className="mb-6 rounded-md border border-red-200 bg-red-50 dark:bg-red-500/10 dark:border-red-500/30 p-4 flex items-start gap-3"
        >
          <Lock size={20} className="text-red-600 mt-0.5" weight="bold" />
          <div>
            <div className="font-semibold text-red-900 dark:text-red-200 text-sm">Account locked</div>
            <div className="text-sm text-red-800 dark:text-red-300 mt-1">
              Your account is restricted until the initial subscription invoice is paid. Pay below to activate full access.
            </div>
          </div>
        </div>
      )}

      {/* Scheduled change banner */}
      {sub.pending_plan_change && (
        <div
          data-testid="pending-plan-banner"
          className="mb-6 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/30 p-4 flex items-center justify-between gap-3 flex-wrap"
        >
          <div className="flex items-start gap-3">
            <Clock size={18} className="text-amber-700 mt-0.5" weight="bold" />
            <div className="text-sm text-amber-900 dark:text-amber-200">
              Scheduled change: <b>{sub.pending_plan_change.to_plan_id?.toUpperCase()}</b> on{" "}
              <b>{(sub.pending_plan_change.effective_at || "").slice(0, 10)}</b>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={cancelScheduled} disabled={busy} data-testid="cancel-scheduled-change-btn">
            Cancel scheduled change
          </Button>
        </div>
      )}

      {/* Current plan card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="md:col-span-2 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-6" data-testid="current-plan-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-md flex items-center justify-center bg-[#002BF6] text-white">
              {React.createElement(PLAN_ICONS[currentPlan.id] || Star, { size: 18, weight: "bold" })}
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Current plan</div>
              <div className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100" style={{ fontFamily: "Work Sans" }}>{currentPlan.name}</div>
            </div>
            <div className="ml-auto text-right">
              <div className="text-3xl font-semibold text-zinc-900 dark:text-zinc-100" style={{ fontFamily: "Work Sans" }}>${currentPlan.price}</div>
              <div className="text-xs text-zinc-500">USD/mo</div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <Metric label="Packages this month" value={sub.packages_this_month} suffix={currentPlan.package_limit_month > 0 ? `/ ${currentPlan.package_limit_month}` : "/ ∞"} />
            <Metric label="Emails sent" value={sub.email_used_this_month} suffix={sub.email_limit_month === -1 ? "/ ∞" : `/ ${sub.email_limit_month}`} />
            <Metric label="Outstanding invoices" value={sub.outstanding_invoice_count} suffix="" testid="outstanding-count" />
          </div>
        </div>

        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-6">
          <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-3">Renewal</div>
          <div className="text-sm text-zinc-900 dark:text-zinc-100">
            {sub.company.plan_expires_at ? new Date(sub.company.plan_expires_at).toLocaleDateString() : "—"}
          </div>
          <div className="text-xs text-zinc-500 mt-1">Next billing cycle</div>
        </div>
      </div>

      {/* Outstanding invoices */}
      <section className="mb-10" data-testid="outstanding-invoices-section">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-lg font-semibold tracking-tight text-zinc-900 dark:text-zinc-100" style={{ fontFamily: "Work Sans" }}>Outstanding</h2>
          <span className="text-xs text-zinc-500">{unpaid.length} unpaid</span>
        </div>
        {unpaid.length === 0 ? (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-6 text-sm text-zinc-500 flex items-center gap-3">
            <Check size={16} className="text-emerald-600" weight="bold" />
            All caught up — no outstanding invoices.
          </div>
        ) : (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 dark:bg-zinc-800/40 border-b border-zinc-200 dark:border-zinc-800">
                <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
                  <th className="px-5 py-3 font-semibold">Invoice #</th>
                  <th className="px-5 py-3 font-semibold">Amount</th>
                  <th className="px-5 py-3 font-semibold">Due</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {unpaid.map((inv) => (
                  <tr key={inv.id} className="border-b border-zinc-100 dark:border-zinc-800 last:border-0" data-testid={`unpaid-inv-${inv.id}`}>
                    <td className="px-5 py-4"><code className="text-xs font-mono bg-zinc-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded">{inv.invoice_number}</code></td>
                    <td className="px-5 py-4 font-semibold text-zinc-900 dark:text-zinc-100">{inv.currency || 'JMD'} ${inv.amount.toLocaleString()}</td>
                    <td className="px-5 py-4 text-xs text-zinc-500">{inv.due_date || "—"}</td>
                    <td className="px-5 py-4"><StatusBadge status={inv.status} /></td>
                    <td className="px-5 py-4 text-right">
                      <Button size="sm" disabled={busy} className="bg-[#002BF6] hover:bg-[#0021C7] text-white" onClick={() => payNow(inv)} data-testid={`pay-now-${inv.id}`}>
                        <Receipt size={14} className="mr-1.5" weight="bold" /> Pay Now
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Change plan */}
      <section className="mb-10">
        <h2 className="text-lg font-semibold tracking-tight text-zinc-900 dark:text-zinc-100 mb-3" style={{ fontFamily: "Work Sans" }}>Change plan</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {sub.all_plans.map((p) => {
            const Icon = PLAN_ICONS[p.id] || Star;
            const isCurrent = p.id === currentPlan.id;
            return (
              <div key={p.id} className={`rounded-md border p-5 bg-white dark:bg-zinc-900 ${isCurrent ? "border-[#002BF6]" : "border-zinc-200 dark:border-zinc-800"}`} data-testid={`plan-card-${p.id}`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center ${isCurrent ? "bg-[#002BF6] text-white" : "bg-zinc-100 dark:bg-zinc-800 text-zinc-700"}`}><Icon size={15} weight="bold" /></div>
                  <div className="text-sm font-semibold uppercase tracking-wider text-zinc-900 dark:text-zinc-100">{p.name}</div>
                  {isCurrent && <span className="ml-auto text-[10px] uppercase tracking-wider font-semibold text-[#002BF6]">Current</span>}
                </div>
                <div className="flex items-baseline gap-1 mb-3">
                  <span className="text-3xl font-semibold text-zinc-900 dark:text-zinc-100" style={{ fontFamily: "Work Sans" }}>${p.price}</span>
                  <span className="text-xs text-zinc-500">USD/mo</span>
                </div>
                <ul className="space-y-1.5 text-xs text-zinc-700 dark:text-zinc-300 mb-4">
                  {p.highlights.slice(0, 4).map((h) => (
                    <li key={h} className="flex items-start gap-1.5"><Check size={11} className="mt-0.5 text-emerald-600 shrink-0" weight="bold" /><span>{h}</span></li>
                  ))}
                </ul>
                {!isCurrent && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    disabled={busy}
                    onClick={() => changePlan(p.id)}
                    data-testid={`change-plan-${p.id}`}
                  >
                    {planRank(p.id) > planRank(currentPlan.id) ? "Upgrade" : "Downgrade"}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-zinc-500 mt-3 flex items-center gap-1.5">
          <WarningCircle size={14} />
          Upgrades take effect immediately. Downgrades schedule for the end of your current billing cycle.
        </p>
      </section>

      {/* Payment history */}
      <section data-testid="payment-history-section">
        <h2 className="text-lg font-semibold tracking-tight text-zinc-900 dark:text-zinc-100 mb-3" style={{ fontFamily: "Work Sans" }}>Payment history</h2>
        {paid.length === 0 ? (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-6 text-sm text-zinc-500">No payments yet.</div>
        ) : (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 dark:bg-zinc-800/40 border-b border-zinc-200 dark:border-zinc-800">
                <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
                  <th className="px-5 py-3 font-semibold">Invoice #</th>
                  <th className="px-5 py-3 font-semibold">Amount</th>
                  <th className="px-5 py-3 font-semibold">Paid on</th>
                </tr>
              </thead>
              <tbody>
                {paid.map((inv) => (
                  <tr key={inv.id} className="border-b border-zinc-100 dark:border-zinc-800 last:border-0">
                    <td className="px-5 py-4"><code className="text-xs font-mono bg-zinc-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded">{inv.invoice_number}</code></td>
                    <td className="px-5 py-4 font-semibold">${inv.amount.toLocaleString()}</td>
                    <td className="px-5 py-4 text-xs text-zinc-500">{(inv.paid_at || inv.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Other ways to pay — collapsible */}
      <OtherWaysToPaySection />
    </div>
  );
}

function OtherWaysToPaySection() {
  const [open, setOpen] = React.useState(false);
  return (
    <section className="mt-8" data-testid="other-ways-to-pay-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md px-5 py-4 hover:border-zinc-300 dark:hover:border-zinc-700 transition-colors"
        data-testid="other-ways-to-pay-toggle"
      >
        <div className="flex items-center gap-3 text-left">
          <div className="w-9 h-9 rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 flex items-center justify-center">
            <Bank size={16} weight="bold" />
          </div>
          <div>
            <div className="font-semibold text-zinc-900 dark:text-zinc-100 text-sm">Other ways to pay</div>
            <div className="text-xs text-zinc-500">Wire transfer or in-branch deposit — for when online payment isn't an option</div>
          </div>
        </div>
        {open ? <CaretUp size={16} weight="bold" className="text-zinc-500" /> : <CaretDown size={16} weight="bold" className="text-zinc-500" />}
      </button>
      {open && (
        <div className="mt-3 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-5 sm:p-6" data-testid="bank-info-panel">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-3">USD Wire / Bank Deposit</div>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
            <BankRow label="Business name" value="Cyrus Field Media Capital Ltd" testid="bank-business-name" />
            <BankRow label="Bank" value="Sagicor Bank — Tropical Plaza" testid="bank-name" />
            <BankRow label="Account type" value="Savings" testid="bank-account-type" />
            <BankRow label="Account #" value="5503986665" testid="bank-account-number" mono />
            <BankRow label="Currency" value="USD" testid="bank-currency" />
          </div>
          <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded text-xs text-amber-900 dark:text-amber-200">
            <b>Important:</b> Include your company name + invoice number as the wire reference so we can apply the payment quickly. Email the deposit slip to <a href="mailto:billing@cyrusfieldtech.com" className="underline">billing@cyrusfieldtech.com</a> for fastest confirmation.
          </div>
        </div>
      )}
    </section>
  );
}

function BankRow({ label, value, testid, mono = false }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* ignore */ }
  };
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-0.5">{label}</div>
      <button type="button" onClick={copy} className="flex items-center gap-2 group" data-testid={testid}>
        <span className={`${mono ? "font-mono" : ""} text-zinc-900 dark:text-zinc-100 font-medium`}>{value}</span>
        <span className="text-[10px] text-zinc-400 opacity-0 group-hover:opacity-100 transition-opacity">{copied ? "Copied!" : "Copy"}</span>
      </button>
    </div>
  );
}

function planRank(id) { return { starter: 0, pro: 1, scale: 2 }[id] ?? 0; }

function Metric({ label, value, suffix, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 mt-1" style={{ fontFamily: "Work Sans" }}>
        {value}
        {suffix && <span className="text-xs text-zinc-500 font-normal ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

/* =====================  Super Admin multi-tenant billing  ===================== */
function SuperAdminBilling() {
  const { setUser } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Outstanding-invoices dialog state (mark a courier's subscription invoice paid via bank transfer)
  const [outstandingCompany, setOutstandingCompany] = useState(null);
  const [outstandingInvoices, setOutstandingInvoices] = useState([]);
  const [outstandingLoading, setOutstandingLoading] = useState(false);
  const [payInv, setPayInv] = useState(null);
  const [payMethod, setPayMethod] = useState("bank_transfer");
  const [payRef, setPayRef] = useState("");
  const [payNote, setPayNote] = useState("");
  const [paySaving, setPaySaving] = useState(false);

  const load = async () => {
    const [p, c] = await Promise.all([
      api.get("/billing/plans"),
      api.get("/billing/companies"),
    ]);
    setPlans(p.data.plans);
    setRows(c.data);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success(`Payment confirmed — plan upgraded!`);
          try { const me = await api.get("/auth/me"); setUser(me.data); } catch (e) {}
          load();
          navigate("/billing", { replace: true });
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout expired.");
          navigate("/billing", { replace: true });
          return;
        }
        if (attempts < 10) setTimeout(poll, 2000);
      } catch (e) {
        toast.error("Could not verify payment");
      }
    };
    poll();
    // eslint-disable-next-line
  }, [params]);

  const startCheckout = async (companyId, planId) => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/billing/checkout", {
        company_id: companyId, plan_id: planId, origin_url: origin,
      });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const toggleLock = async (company) => {
    const next = !company.account_locked;
    if (!confirm(`${next ? "Lock" : "Unlock"} ${company.name}?`)) return;
    try {
      await api.post(`/admin/companies/${company.id}/lock`, { locked: next });
      toast.success(next ? "Company locked" : "Company unlocked");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const openOutstanding = async (company) => {
    setOutstandingCompany(company);
    setOutstandingLoading(true);
    setOutstandingInvoices([]);
    try {
      const { data } = await api.get(`/billing/companies/${company.id}/outstanding-invoices`);
      setOutstandingInvoices(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load invoices");
    } finally { setOutstandingLoading(false); }
  };

  const submitOfflinePay = async () => {
    if (!payInv) return;
    setPaySaving(true);
    try {
      await api.post(`/invoices/${payInv.id}/mark-paid-offline`, {
        payment_method: payMethod,
        payment_reference: payRef.trim() || null,
        note: payNote.trim() || null,
      });
      toast.success(`${payInv.invoice_number} marked paid (${payMethod.replace("_", " ")})`);
      setPayInv(null);
      setPayRef("");
      setPayNote("");
      setPayMethod("bank_transfer");
      // Refresh both lists
      load();
      if (outstandingCompany) openOutstanding(outstandingCompany);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setPaySaving(false); }
  };

  // Run the dunning runner manually (super admin can dispatch overdue-invoice
  // reminders + auto-lock without waiting for the hourly background loop)
  const [dunningRunning, setDunningRunning] = useState(false);
  const runDunning = async (companyIdOnly = null) => {
    if (dunningRunning) return;
    setDunningRunning(true);
    try {
      const params = companyIdOnly ? { company_id: companyIdOnly } : {};
      const { data } = await api.post("/admin/invoices/run-overdue-reminders", null, { params });
      const totalSent = (data.stage_1 || 0) + (data.stage_2 || 0) + (data.stage_3 || 0);
      if (totalSent === 0 && (data.auto_locked || 0) === 0) {
        toast.message("No reminders due — all invoices within their grace window.");
      } else {
        toast.success(
          `Sent ${totalSent} reminder${totalSent === 1 ? "" : "s"}` +
          (data.auto_locked ? ` · ${data.auto_locked} auto-locked` : "")
        );
      }
      load();
      if (outstandingCompany) openOutstanding(outstandingCompany);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to run reminders");
    } finally { setDunningRunning(false); }
  };

  // Manual trigger for the daily subscription-renewal invoice generator.
  // Skips inactive / locked tenants automatically on the backend.
  const [renewalRunning, setRenewalRunning] = useState(false);
  const runRenewals = async (companyIdOnly = null) => {
    if (renewalRunning) return;
    setRenewalRunning(true);
    try {
      const params = companyIdOnly ? { company_id: companyIdOnly } : {};
      const { data } = await api.post("/admin/invoices/run-renewal-generator", null, { params });
      const gen = data.generated || 0;
      if (gen === 0) {
        toast.message(
          `No renewals due · scanned ${data.scanned || 0} · ` +
          `skipped ${data.skipped_locked || 0} locked, ${data.skipped_inactive || 0} inactive`
        );
      } else {
        toast.success(`Generated ${gen} renewal invoice${gen === 1 ? "" : "s"}`);
      }
      load();
      if (outstandingCompany) openOutstanding(outstandingCompany);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to run renewals");
    } finally { setRenewalRunning(false); }
  };

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]" data-testid="billing-page">
      <Toaster position="top-right" />
      <div className="mb-8">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">Revenue</div>
        <h1 className="text-3xl font-semibold tracking-tight mt-1 text-zinc-900 dark:text-zinc-100" style={{ fontFamily: "Work Sans" }}>
          Billing & Subscriptions
        </h1>
        <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">Manage plans for each courier company tenant.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        {plans.map((p) => {
          const Icon = PLAN_ICONS[p.id] || Star;
          const isScale = p.id === "scale";
          return (
            <div key={p.id}
              className={`relative rounded-md border p-6 bg-white dark:bg-zinc-900 transition-colors ${isScale ? "border-[#002BF6]" : "border-zinc-200 dark:border-zinc-800"}`}
              data-testid={`plan-card-${p.id}`}>
              {isScale && (
                <div className="absolute -top-2 right-4 bg-[#002BF6] text-white text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold">Most powerful</div>
              )}
              <div className="flex items-center gap-2 mb-4">
                <div className={`w-8 h-8 rounded-md flex items-center justify-center ${isScale ? "bg-[#002BF6] text-white" : "bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200"}`}>
                  <Icon size={16} weight="bold" />
                </div>
                <div className="text-sm font-semibold uppercase tracking-wider text-zinc-900 dark:text-zinc-100">{p.name}</div>
              </div>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-4xl font-semibold text-zinc-900 dark:text-zinc-100" style={{ fontFamily: "Work Sans" }}>${p.price}</span>
                <span className="text-sm text-zinc-500">USD/mo</span>
              </div>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">{p.description}</p>
              <ul className="space-y-2 text-sm text-zinc-700 dark:text-zinc-300">
                {p.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-2">
                    <Check size={14} className="mt-0.5 text-emerald-600 shrink-0" weight="bold" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md overflow-x-auto">
        <div className="px-5 py-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500 dark:text-zinc-400">Tenant Subscriptions</div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={() => runRenewals()}
              disabled={renewalRunning}
              data-testid="run-renewals-btn"
              className="text-xs"
              title="Generate monthly subscription invoices for tenants whose plan renews in the next 3 days. Skips inactive / locked tenants."
            >
              {renewalRunning ? "Running…" : (<><ArrowClockwise size={12} className="mr-1.5" /> Generate renewals</>)}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => runDunning()}
              disabled={dunningRunning}
              data-testid="run-dunning-btn"
              className="text-xs"
              title="Send overdue-invoice reminder emails to any tenant past the grace window"
            >
              {dunningRunning ? "Running…" : (<><WarningCircle size={12} className="mr-1.5" /> Run overdue reminders</>)}
            </Button>
          </div>
        </div>
        <table className="w-full text-sm min-w-[640px]" data-testid="billing-table">
          <thead className="bg-zinc-50 dark:bg-zinc-800/50 border-b border-zinc-200 dark:border-zinc-800">
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
              <th className="px-5 py-3 font-semibold">Company</th>
              <th className="px-5 py-3 font-semibold">Plan</th>
              <th className="px-5 py-3 font-semibold">Usage this month</th>
              <th className="px-5 py-3 font-semibold">Renews</th>
              <th className="px-5 py-3 font-semibold">Outstanding</th>
              <th className="px-5 py-3 font-semibold">Access</th>
              <th className="px-5 py-3 font-semibold text-right">Change plan</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const limit = r.plan.package_limit_month;
              const pct = limit > 0 ? Math.min(100, Math.round((r.packages_this_month / limit) * 100)) : 0;
              const locked = !!r.company.account_locked;
              return (
                <tr key={r.company.id} className="border-b border-zinc-100 dark:border-zinc-800 last:border-0" data-testid={`billing-row-${r.company.id}`}>
                  <td className="px-5 py-4 font-medium text-zinc-900 dark:text-zinc-100">{r.company.name}</td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      r.plan.id === "scale" ? "bg-[#002BF6]/10 text-[#002BF6] border border-[#002BF6]/20" :
                      r.plan.id === "pro" ? "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/20" :
                      "bg-zinc-100 text-zinc-700 border border-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700"
                    }`}>
                      {r.plan.name}
                    </span>
                  </td>
                  <td className="px-5 py-4 min-w-[220px]">
                    {limit > 0 ? (
                      <>
                        <div className="text-xs text-zinc-600 dark:text-zinc-400 mb-1"><b>{r.packages_this_month}</b> / {limit} packages</div>
                        <div className="h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded overflow-hidden">
                          <div className="h-full bg-[#002BF6] transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </>
                    ) : (
                      <div className="text-xs text-zinc-600 dark:text-zinc-400"><b>{r.packages_this_month}</b> packages · unlimited</div>
                    )}
                  </td>
                  <td className="px-5 py-4 text-xs text-zinc-500 dark:text-zinc-400">{r.company.plan_expires_at ? new Date(r.company.plan_expires_at).toLocaleDateString() : "—"}</td>
                  <td className="px-5 py-4">
                    {(r.outstanding_invoice_count || 0) === 0 ? (
                      <span className="text-xs text-zinc-400" data-testid={`outstanding-${r.company.id}`}>None</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => openOutstanding(r.company)}
                        className="text-left group"
                        data-testid={`outstanding-${r.company.id}`}
                        title="Click to view & record bank transfer"
                      >
                        <div className="text-xs font-semibold text-red-700 dark:text-red-400 group-hover:underline">
                          {r.outstanding_invoice_count} unpaid
                        </div>
                        <div className="text-[10px] text-zinc-500 dark:text-zinc-400 font-mono">
                          {Object.entries(r.outstanding_by_currency || {}).map(([cur, amt]) => (
                            <span key={cur} className="mr-2">{cur} ${Number(amt).toLocaleString()}</span>
                          ))}
                        </div>
                      </button>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <Button
                      variant={locked ? "destructive" : "outline"}
                      size="sm"
                      onClick={() => toggleLock(r.company)}
                      data-testid={`lock-toggle-${r.company.id}`}
                      className="text-xs"
                    >
                      <Lock size={11} className="mr-1" /> {locked ? "Locked" : "Active"}
                    </Button>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="inline-flex gap-1.5">
                      {plans.filter(p => p.id !== r.plan.id).map((p) => (
                        <Button key={p.id} variant="outline" size="sm" disabled={loading}
                          onClick={() => startCheckout(r.company.id, p.id)}
                          data-testid={`upgrade-${r.company.id}-${p.id}`}
                          className="text-xs">
                          <ArrowUp size={11} className="mr-1" /> {p.name}
                        </Button>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Outstanding invoices list dialog */}
      <Dialog open={!!outstandingCompany} onOpenChange={(o) => { if (!o) { setOutstandingCompany(null); setOutstandingInvoices([]); } }}>
        <DialogContent className="max-w-2xl" data-testid="outstanding-dialog">
          <DialogHeader>
            <DialogTitle>Outstanding invoices · {outstandingCompany?.name}</DialogTitle>
          </DialogHeader>
          {outstandingLoading ? (
            <div className="text-center text-sm text-zinc-500 py-8">Loading…</div>
          ) : outstandingInvoices.length === 0 ? (
            <div className="text-center text-sm text-zinc-500 py-8">No outstanding invoices — all caught up.</div>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="outstanding-list">
              {outstandingInvoices.map((inv) => {
                const stage = inv.reminder_stage || 0;
                const stageLabels = {
                  1: { label: "Reminder #1 sent", color: "bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700" },
                  2: { label: "Warning sent", color: "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30" },
                  3: { label: "Final notice + locked", color: "bg-red-50 text-red-800 border-red-200 dark:bg-red-500/10 dark:text-red-300 dark:border-red-500/30" },
                };
                const sBadge = stageLabels[stage];
                return (
                <div key={inv.id} className="flex items-center justify-between gap-3 rounded-md border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-4 py-3" data-testid={`outstanding-inv-${inv.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <code className="text-xs font-mono bg-zinc-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded">{inv.invoice_number}</code>
                      {inv.is_subscription && (
                        <span className="text-[10px] uppercase tracking-wider font-semibold bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-300 border border-violet-200 dark:border-violet-500/30 rounded-full px-2 py-0.5">Subscription</span>
                      )}
                      {sBadge && (
                        <span className={`text-[10px] uppercase tracking-wider font-semibold border rounded-full px-2 py-0.5 ${sBadge.color}`} title={inv.last_reminder_at ? `Last sent ${new Date(inv.last_reminder_at).toLocaleString()}` : ""} data-testid={`reminder-stage-${inv.id}`}>
                          {sBadge.label}
                        </span>
                      )}
                    </div>
                    <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">{inv.customer_name}</div>
                    <div className="text-[11px] text-zinc-500 dark:text-zinc-400">
                      Created {new Date(inv.created_at).toLocaleDateString()}
                      {inv.last_reminder_at && (
                        <> · Last reminder {new Date(inv.last_reminder_at).toLocaleDateString()}</>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{inv.currency || "JMD"} ${Number(inv.amount).toLocaleString()}</div>
                    <Button
                      size="sm"
                      className="mt-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => { setPayInv(inv); setPayRef(""); setPayNote(""); setPayMethod("bank_transfer"); }}
                      data-testid={`pay-outstanding-${inv.id}`}
                    >
                      <Check size={12} weight="bold" className="mr-1" /> Mark paid
                    </Button>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Record offline payment dialog */}
      <Dialog open={!!payInv} onOpenChange={(o) => { if (!o) setPayInv(null); }}>
        <DialogContent className="max-w-md" data-testid="offline-pay-dialog-billing">
          <DialogHeader>
            <DialogTitle>Record offline payment</DialogTitle>
          </DialogHeader>
          {payInv && (
            <div className="space-y-4">
              <div className="rounded-md border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 px-3 py-2.5 text-sm">
                <div className="flex items-baseline justify-between gap-3">
                  <code className="text-xs font-mono bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded px-1.5 py-0.5">{payInv.invoice_number}</code>
                  <span className="font-semibold text-zinc-900 dark:text-zinc-100">{payInv.currency || "JMD"} ${Number(payInv.amount).toLocaleString()}</span>
                </div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 truncate">{payInv.customer_name}</div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Payment method</Label>
                <Select value={payMethod} onValueChange={setPayMethod}>
                  <SelectTrigger className="mt-1.5" data-testid="billing-offline-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Reference (optional)</Label>
                <Input
                  className="mt-1.5"
                  value={payRef}
                  onChange={(e) => setPayRef(e.target.value)}
                  placeholder="Wire confirmation, bank slip #, cheque #…"
                  data-testid="billing-offline-reference"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Internal note (optional)</Label>
                <Textarea
                  rows={2}
                  className="mt-1.5"
                  value={payNote}
                  onChange={(e) => setPayNote(e.target.value)}
                  placeholder="Audit trail notes…"
                  data-testid="billing-offline-note"
                />
              </div>
              <div className="rounded-md border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 px-3 py-2 text-xs text-emerald-800 dark:text-emerald-300">
                Submitting marks this invoice paid and (if applicable) auto-unlocks the company's account.
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPayInv(null)} disabled={paySaving}>Cancel</Button>
            <Button
              onClick={submitOfflinePay}
              disabled={paySaving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="billing-offline-confirm"
            >
              {paySaving ? "Saving…" : (<><Check size={14} className="mr-1" /> Confirm payment</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
